/**
 * Дивергенция: цена и импульс разошлись.
 *
 * Три вида расхождения, и говорят они о разном.
 *
 * **Обычная** — цена ставит новый экстремум, импульс не подтверждает.
 * Читается как «движение продолжается, но платить за него стали
 * меньше»; ищут по ней разворот.
 *
 * **Скрытая** — наоборот: цена откатилась не так глубоко, как импульс.
 * Это не слабость, а передышка внутри тренда, и ищут по ней
 * продолжение, а не разворот. Путать их дорого: одна и та же картинка
 * на графике означает вход в противоположные стороны.
 *
 * **Тройная** — то же расхождение, но выдержанное на трёх экстремумах
 * подряд. Встречается заметно реже и потому не заменяет обычную, а
 * стоит рядом: кому нужен редкий и веский сигнал, берёт её.
 *
 * Три решения, на которых держится честность расчёта.
 *
 * **Сравниваются вершины, а не бары.** Расхождение живёт между двумя
 * экстремумами; сравнивать соседние бары значит ловить рябь. Экстремумы
 * берутся те же, что у всей структуры, — `swings`, с тем же окном
 * подтверждения.
 *
 * **Событие возникает там, где стало известно, а не там, где случилось.**
 * Экстремум подтверждается только через `right` баров после себя.
 * Пометить расхождение задним числом, на самой вершине, значило бы
 * показать на графике сигнал, которого в тот момент никто не мог
 * видеть, — и проверка на истории врала бы в свою пользу.
 *
 * **Далёкие вершины не пара.** Между экстремумами ставится предел в
 * барах: расхождение с вершиной месячной давности формально считается,
 * но к текущему движению отношения не имеет.
 */
import { swings } from './structure.js';
export function divergences(bars, momentum, options) {
    const out = {
        up: blank(bars.length),
        down: blank(bars.length),
        hiddenUp: blank(bars.length),
        hiddenDown: blank(bars.length),
        tripleUp: blank(bars.length),
        tripleDown: blank(bars.length),
    };
    if (bars.length === 0)
        return out;
    const pivots = swings(bars, options.left, options.right);
    const limit = options.maxGap > 0 ? Math.floor(options.maxGap) : Infinity;
    /** Последние подтверждённые впадины и вершины, свежая в конце. */
    const lows = [];
    const highs = [];
    for (let i = 0; i < bars.length; i += 1) {
        /*
         * На прогреве импульса стоит пустота, а не ноль.
         *
         * Ноль здесь означал бы «расхождения не было», хотя на деле его
         * было нечем искать: RSI ещё не посчитан. Разница видна ровно
         * тогда, когда правило срабатывает на пустом месте.
         */
        const known = momentum[i] !== null && momentum[i] !== undefined;
        if (known)
            fill(out, i, 0);
        const fresh = confirmed(bars, momentum, pivots, i);
        if (!known || !fresh)
            continue;
        if (fresh.kind === 'low') {
            keep(lows, fresh.pivot);
            const { предыдущая, позапрошлая } = соседи(lows);
            if (предыдущая && близко(предыдущая, fresh.pivot, limit)) {
                // Цена ниже, импульс выше — расхождение в пользу покупателя.
                if (fresh.pivot.price < предыдущая.price && fresh.pivot.momentum > предыдущая.momentum) {
                    out.up[i] = 1;
                }
                // Цена выше, импульс ниже — откат мельче, чем кажется.
                if (fresh.pivot.price > предыдущая.price && fresh.pivot.momentum < предыдущая.momentum) {
                    out.hiddenUp[i] = 1;
                }
            }
            if (позапрошлая &&
                предыдущая &&
                близко(позапрошлая, fresh.pivot, limit) &&
                fresh.pivot.price < предыдущая.price &&
                предыдущая.price < позапрошлая.price &&
                fresh.pivot.momentum > предыдущая.momentum &&
                предыдущая.momentum > позапрошлая.momentum) {
                out.tripleUp[i] = 1;
            }
        }
        else {
            keep(highs, fresh.pivot);
            const { предыдущая, позапрошлая } = соседи(highs);
            if (предыдущая && близко(предыдущая, fresh.pivot, limit)) {
                if (fresh.pivot.price > предыдущая.price && fresh.pivot.momentum < предыдущая.momentum) {
                    out.down[i] = 1;
                }
                if (fresh.pivot.price < предыдущая.price && fresh.pivot.momentum > предыдущая.momentum) {
                    out.hiddenDown[i] = 1;
                }
            }
            if (позапрошлая &&
                предыдущая &&
                близко(позапрошлая, fresh.pivot, limit) &&
                fresh.pivot.price > предыдущая.price &&
                предыдущая.price > позапрошлая.price &&
                fresh.pivot.momentum < предыдущая.momentum &&
                предыдущая.momentum < позапрошлая.momentum) {
                out.tripleDown[i] = 1;
            }
        }
    }
    return out;
}
/* ---------------------------------------------------------- частное */
function blank(length) {
    return new Array(length).fill(null);
}
function fill(series, at, value) {
    series.up[at] = value;
    series.down[at] = value;
    series.hiddenUp[at] = value;
    series.hiddenDown[at] = value;
    series.tripleUp[at] = value;
    series.tripleDown[at] = value;
}
/**
 * Экстремум, подтвердившийся именно на этом баре.
 *
 * Новый узнаётся по смене индекса, а не по смене цены: две вершины
 * могут стоять на одной цене, и это две вершины, а не одна.
 */
function confirmed(bars, momentum, pivots, i) {
    const lowAt = pivots.lowAt[i] ?? null;
    if (lowAt !== null && lowAt !== (pivots.lowAt[i - 1] ?? null)) {
        const pivot = pivotAt(bars, momentum, lowAt, 'low');
        if (pivot)
            return { kind: 'low', pivot };
    }
    const highAt = pivots.highAt[i] ?? null;
    if (highAt !== null && highAt !== (pivots.highAt[i - 1] ?? null)) {
        const pivot = pivotAt(bars, momentum, highAt, 'high');
        if (pivot)
            return { kind: 'high', pivot };
    }
    return null;
}
/**
 * Экстремум вместе со значением импульса на нём.
 *
 * Пустой импульс на баре вершины — не повод считать расхождение
 * нулевым: на прогреве осциллятора его просто ещё нет, и сравнивать
 * не с чем.
 */
function pivotAt(bars, momentum, at, kind) {
    const bar = bars[at];
    const value = momentum[at];
    if (!bar || value === null || value === undefined)
        return null;
    return { at, price: kind === 'low' ? bar.low : bar.high, momentum: value };
}
/** Держим три последних: больше для тройного расхождения не нужно. */
function keep(list, pivot) {
    list.push(pivot);
    if (list.length > 3)
        list.shift();
}
/**
 * Соседи свежего экстремума — тот, что был до него, и тот, что до того.
 *
 * Свежий уже лежит последним, и брать его отсюда незачем: сравнивать
 * вершину саму с собой — как раз та ошибка, из-за которой предел
 * расстояния однажды перестал действовать вовсе.
 */
function соседи(list) {
    return { предыдущая: list.at(-2) ?? null, позапрошлая: list.at(-3) ?? null };
}
function близко(from, to, limit) {
    return to.at - from.at <= limit;
}
//# sourceMappingURL=divergence.js.map